import {
	AlertCircle,
	ArrowLeft,
	CheckCircle,
	ChevronDown,
	ChevronRight,
	Database,
	Plus,
	RefreshCw,
	Save,
	Trash2,
	X,
} from "lucide-react"
import { useEffect, useState } from "react"
import { adminKeys } from "../../search-standalone/services/adminKeys"
import { TokenManager } from "../../search-standalone/services/TokenManager"
import { AdminKeysPanel, useAdminKey } from "./AdminKeysPanel"
import { CLERK_ENABLED } from "./AdminSignIn"
import { cn } from "../../utils/cn"
import { useSearchClient } from "./SearchProvider"
import { ProductEnrichmentStudio } from "./ProductEnrichmentStudio"
import { ShortlistSettings } from "./ShortlistSettings"
import { SmartSearchSettings } from "./SmartSearchSettings"
import { BrandThemeSettings } from "./BrandThemeSettings"
import { BrandPreviewSettings } from "./BrandPreviewSettings"

interface SettingsPageProps {
	organizationId: string
	onBack: () => void
	storeUrl?: string
}

interface CollectionInfo {
	name: string
	physicalName?: string
	isSelected?: boolean
	numDocuments: number
	createdAt: number
	fields: CollectionField[]
	defaultSortingField?: string
	organizationStats?: {
		[orgId: string]: number
	}
}

interface CollectionSearchConfigDto {
	defaultSearchMethod: "keyword" | "semantic" | "hybrid" | "natural_language"
	searchCapabilities: {
		keyword: boolean
		semantic: boolean
		hybrid: boolean
		naturalLanguage: boolean
	}
	defaultCurrency?: string
	vectorQueryConfig?: {
		semantic?: {
			enabled?: boolean
			k?: number
			distanceThreshold?: number
			alpha?: number
			rerankHybridMatches?: boolean
		}
		hybrid?: {
			enabled?: boolean
			k?: number
			distanceThreshold?: number
			alpha?: number
			rerankHybridMatches?: boolean
		}
	}
	embeddingConfig?: any
	nlSearchConfig?: any
}

interface CollectionField {
	name: string
	type: string
	facet: boolean
	optional: boolean
	index: boolean
}

interface CollectionHealth {
	name: string
	healthy: boolean
	error?: string
}

interface SampleDocument {
	[key: string]: any
}

interface IntentChip {
	label: string
	query: string
	category?: string
}

interface NewChipInput {
	label: string
	query: string
	category: string
}

const DEFAULT_SEARCH_PLACEHOLDERS = [
	"Search dresses for pear-shaped body",
	"Shop festive wear under ₹5,000",
	"Find similar styles",
	"Track my order",
	"Wedding guest outfits",
]

const MAX_SEARCH_PLACEHOLDERS = 10

export const SettingsPage = ({ organizationId, onBack, storeUrl }: SettingsPageProps) => {
	const { refreshCollections } = useSearchClient()
	const operatorKey = useAdminKey("operator")
	const [isLoading, setIsLoading] = useState(false)
	const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null)
	const [isPublishing, setIsPublishing] = useState(false)
	const [publishUrlInput, setPublishUrlInput] = useState(storeUrl || "")
	const [uploadStats, setUploadStats] = useState<{ count: number } | null>(null)
	const [reindexStats, setReindexStats] = useState<{ products: number; suggestions: number } | null>(null)

	// Collection management state
	const [collections, setCollections] = useState<CollectionInfo[]>([])
	const [selectedCollections, setSelectedCollections] = useState<string[]>([])
	const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set())
	const [collectionHealth, setCollectionHealth] = useState<Map<string, CollectionHealth>>(new Map())
	const [collectionSamples, setCollectionSamples] = useState<Map<string, SampleDocument[]>>(new Map())
	const [isLoadingCollections, setIsLoadingCollections] = useState(false)
	const [showAllCollections, setShowAllCollections] = useState(false)
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [newCollectionName, setNewCollectionName] = useState("")
	const [newCollectionTemplate, setNewCollectionTemplate] = useState<string>("products")
	const [newCollectionDefaultCurrency, setNewCollectionDefaultCurrency] = useState("")
	const [collectionSearchConfigs, setCollectionSearchConfigs] = useState<Map<string, CollectionSearchConfigDto>>(new Map())
	const [savingCollectionCurrency, setSavingCollectionCurrency] = useState<Set<string>>(new Set())
	const [availableTemplates, setAvailableTemplates] = useState<Array<{ name: string; description: string }>>([])
	const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)

	const getOrgCollectionPrefix = () => `org_${organizationId.replace(/[^a-zA-Z0-9_-]/g, "_")}__`

	const getPhysicalCollectionName = (collection: CollectionInfo) => collection.physicalName || collection.name

	const getLogicalCollectionName = (collection: CollectionInfo) => {
		if (collection.physicalName) {
			return collection.name
		}

		const prefix = getOrgCollectionPrefix()
		if (collection.name.startsWith(prefix)) {
			return collection.name.slice(prefix.length)
		}

		return collection.name
	}

	const collectionBelongsToCurrentOrg = (collection: CollectionInfo) => {
		const physicalName = getPhysicalCollectionName(collection)
		return physicalName.startsWith(getOrgCollectionPrefix())
	}

	// Search configuration state
	const [enableKeyword, setEnableKeyword] = useState(true)
	const [enableSemantic, setEnableSemantic] = useState(false)
	const [enableHybrid, setEnableHybrid] = useState(false)
	const [enableNaturalLanguage, setEnableNaturalLanguage] = useState(false)
	const [defaultMethod, setDefaultMethod] = useState<"keyword" | "semantic" | "hybrid" | "natural_language">("keyword")

	// Embedding configuration state
	const [embeddingProvider, setEmbeddingProvider] = useState<"openai" | "google" | "azure" | "custom">("openai")
	const [embeddingModel, setEmbeddingModel] = useState("text-embedding-3-small")
	const [embeddingApiKey, setEmbeddingApiKey] = useState("")
	const [semanticVectorEnabled, setSemanticVectorEnabled] = useState(false)
	const [semanticVectorK, setSemanticVectorK] = useState("100")
	const [semanticVectorDistanceThreshold, setSemanticVectorDistanceThreshold] = useState("")
	const [hybridVectorEnabled, setHybridVectorEnabled] = useState(false)
	const [hybridVectorK, setHybridVectorK] = useState("100")
	const [hybridVectorDistanceThreshold, setHybridVectorDistanceThreshold] = useState("")
	const [hybridVectorAlpha, setHybridVectorAlpha] = useState("0.3")
	const [hybridRerankMatches, setHybridRerankMatches] = useState(false)

	// Natural Language configuration state
	const [nlModelId, setNlModelId] = useState("openai-default")
	const [availableNLModels, setAvailableNLModels] = useState<
		Array<{ id: string; provider: string; modelName: string; description: string }>
	>([])

	// Intent chips state (per collection)
	const [collectionIntentChips, setCollectionIntentChips] = useState<Map<string, IntentChip[]>>(new Map())
	const [intentChipsSaving, setIntentChipsSaving] = useState<Set<string>>(new Set())
	const [newChipInputs, setNewChipInputs] = useState<Map<string, NewChipInput>>(new Map())

	// Search placeholders state (org-level)
	const [searchPlaceholders, setSearchPlaceholders] = useState<string[]>([])
	const [isLoadingSearchPlaceholders, setIsLoadingSearchPlaceholders] = useState(false)
	const [isSavingSearchPlaceholders, setIsSavingSearchPlaceholders] = useState(false)

	// Load collections and templates on mount
	useEffect(() => {
		loadCollections()
		loadSelectedCollections()
		loadAvailableNLModels()
		loadSearchPlaceholders()
	}, [organizationId])

	useEffect(() => {
		loadCollections(showAllCollections)
	}, [showAllCollections])

	useEffect(() => {
		if (storeUrl) {
			setPublishUrlInput(storeUrl)
		}
	}, [storeUrl])

	const normalizePublishUrl = (rawUrl: string) => {
		const value = rawUrl.trim()
		if (!value) {
			return ""
		}
		return value.startsWith("http://") || value.startsWith("https://") ? value : `https://${value}`
	}

	const handlePublish = async () => {
		const targetUrl = normalizePublishUrl(publishUrlInput)
		if (!targetUrl) {
			setStatus({ type: "error", message: "Store URL is required before publishing." })
			return
		}

		setIsPublishing(true)
		setStatus(null)

		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			if (!adminKeys.get("admin") && !adminKeys.get("operator") && !adminKeys.signedIn()) {
				throw new Error("Sign in, or enter the store admin key above, to publish")
			}

			const authHeader = await getAuthHeader()
			const response = await fetch(`${backendUrl}/api/v1/search/publish`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...authHeader,
				},
				body: JSON.stringify({ storeUrl: targetUrl }),
			})

			const result = await response.json().catch(() => ({}))
			if (!response.ok) {
				throw new Error(result.error || "Failed to publish")
			}

			const remotePath = result?.result?.remote?.remotePath
			const dirAlreadyExisted = result?.result?.remote?.remoteDirectoryAlreadyExisted === true
			const dirCreated = result?.result?.remote?.remoteDirectoryCreated === true
			const registryUpdated = result?.result?.registry?.updated === true

			const statusSegments: string[] = []
			if (remotePath) {
				if (dirAlreadyExisted) {
					statusSegments.push(`Remote directory already existed at ${remotePath}`)
				} else if (dirCreated) {
					statusSegments.push(`Remote directory created at ${remotePath}`)
				} else {
					statusSegments.push(`Remote directory ready at ${remotePath}`)
				}
			}

			if (registryUpdated) {
				statusSegments.push("store-registry.json updated")
			}
			setStatus({
				type: "success",
				message:
					statusSegments.length > 0
						? `Published successfully. ${statusSegments.join(". ")}. Opening store URL...`
						: "Published successfully. Opening store URL for demo...",
			})
			window.open(targetUrl, "_blank", "noopener,noreferrer")
		} catch (error) {
			setStatus({
				type: "error",
				message: error instanceof Error ? error.message : "Failed to publish. Please try again.",
			})
		} finally {
			setIsPublishing(false)
		}
	}

	// Load templates when modal opens
	useEffect(() => {
		if (showCreateModal) {
			loadAvailableTemplates()
		}
	}, [showCreateModal])

	const loadAvailableTemplates = async () => {
		setIsLoadingTemplates(true)
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const response = await fetch(`${backendUrl}/api/v1/search/collections/templates`)
			const result = await response.json()

			if (response.ok && result.success && result.templates) {
				setAvailableTemplates(result.templates)
				// Set default template if current selection is not available
				if (result.templates.length > 0 && !result.templates.some((t: any) => t.name === newCollectionTemplate)) {
					setNewCollectionTemplate(result.templates[0].name)
				}
			} else {
				// Fallback to default templates if API fails
				setAvailableTemplates([
					{ name: "products", description: "Default product template" },
					{ name: "suggestions", description: "Query suggestions template" },
					{ name: "shopify_collections", description: "Shopify collections template" },
					{ name: "shopify_faqs", description: "Shopify FAQs template" },
					{ name: "shopify_policies", description: "Shopify policies template" },
					{ name: "shopify_brand_story", description: "Shopify brand story template" },
					{ name: "shopify_testimonials", description: "Shopify testimonials template" },
					{ name: "shopify_support_info", description: "Shopify support info template" },
				])
			}
		} catch (error) {
			console.error("Failed to load templates:", error)
			// Fallback to default templates on error
			setAvailableTemplates([
				{ name: "products", description: "Default product template" },
				{ name: "suggestions", description: "Query suggestions template" },
				{ name: "shopify_collections", description: "Shopify collections template" },
				{ name: "shopify_faqs", description: "Shopify FAQs template" },
				{ name: "shopify_policies", description: "Shopify policies template" },
				{ name: "shopify_brand_story", description: "Shopify brand story template" },
				{ name: "shopify_testimonials", description: "Shopify testimonials template" },
				{ name: "shopify_support_info", description: "Shopify support info template" },
			])
		} finally {
			setIsLoadingTemplates(false)
		}
	}

	// Helper function to get auth header for protected endpoints
	const getAuthHeader = async () => {
		const token = await TokenManager.getInstance().getBackendToken()
		// Settings changes also need an admin key; the org token alone is public.
		return { Authorization: `Bearer ${token}`, ...(await adminKeys.authHeaders()) }
	}

	const sanitizeSearchPlaceholders = (items: string[]): string[] => {
		const dedupe = new Set<string>()
		const cleaned: string[] = []

		for (const raw of items) {
			const value = (raw || "").trim().slice(0, 120)
			if (!value) continue
			const key = value.toLowerCase()
			if (dedupe.has(key)) continue
			dedupe.add(key)
			cleaned.push(value)
			if (cleaned.length >= MAX_SEARCH_PLACEHOLDERS) break
		}

		return cleaned
	}

	const loadSearchPlaceholders = async () => {
		setIsLoadingSearchPlaceholders(true)
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const response = await fetch(
				`${backendUrl}/api/v1/search/config/search-placeholders?organizationId=${organizationId}`,
			)
			const result = await response.json()

			if (response.ok && result.success && Array.isArray(result.placeholders)) {
				const cleaned = sanitizeSearchPlaceholders(result.placeholders)
				setSearchPlaceholders(cleaned.length > 0 ? cleaned : [...DEFAULT_SEARCH_PLACEHOLDERS])
			} else {
				setSearchPlaceholders([...DEFAULT_SEARCH_PLACEHOLDERS])
			}
		} catch (error) {
			console.error("Failed to load search placeholders:", error)
			setSearchPlaceholders([...DEFAULT_SEARCH_PLACEHOLDERS])
		} finally {
			setIsLoadingSearchPlaceholders(false)
		}
	}

	const saveSearchPlaceholders = async () => {
		const cleaned = sanitizeSearchPlaceholders(searchPlaceholders)
		if (cleaned.length === 0) {
			setStatus({ type: "error", message: "Please keep at least one valid search placeholder" })
			return
		}

		setIsSavingSearchPlaceholders(true)
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(
				`${backendUrl}/api/v1/search/config/search-placeholders?organizationId=${organizationId}`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						...authHeader,
					},
					body: JSON.stringify({ placeholders: cleaned }),
				},
			)
			const result = await response.json()

			if (!response.ok || !result.success) {
				throw new Error(result.error || "Failed to save search placeholders")
			}

			const saved = sanitizeSearchPlaceholders(result.placeholders || cleaned)
			setSearchPlaceholders(saved.length > 0 ? saved : cleaned)
			setStatus({ type: "success", message: "Search placeholders saved successfully" })
		} catch (error) {
			setStatus({
				type: "error",
				message: error instanceof Error ? error.message : "Failed to save search placeholders",
			})
		} finally {
			setIsSavingSearchPlaceholders(false)
		}
	}

	const updateSearchPlaceholder = (index: number, value: string) => {
		setSearchPlaceholders((prev) => prev.map((item, i) => (i === index ? value : item)))
	}

	const addSearchPlaceholder = () => {
		setSearchPlaceholders((prev) => {
			if (prev.length >= MAX_SEARCH_PLACEHOLDERS) return prev
			return [...prev, ""]
		})
	}

	const removeSearchPlaceholder = (index: number) => {
		setSearchPlaceholders((prev) => {
			if (prev.length <= 1) return prev
			return prev.filter((_, i) => i !== index)
		})
	}

	const resetSearchPlaceholders = () => {
		setSearchPlaceholders([...DEFAULT_SEARCH_PLACEHOLDERS])
		setStatus({ type: "success", message: "Reset to default search placeholders. Click Save to persist." })
	}

	const loadAvailableNLModels = async () => {
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const response = await fetch(`${backendUrl}/api/v1/search/nl-models`)
			const result = await response.json()

			if (response.ok && result.success) {
				setAvailableNLModels(result.models)
				// Set default model if available
				if (result.models.length > 0) {
					setNlModelId(result.models[0].id)
				}
			}
		} catch (error) {
			console.error("Failed to load NL models:", error)
		}
	}

	const loadCollections = async (allCollectionsScope: boolean = showAllCollections) => {
		setIsLoadingCollections(true)
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const endpoint = allCollectionsScope ? "/api/v1/search/collections" : "/api/v1/search/collections/for-org"
			const response = await fetch(`${backendUrl}${endpoint}`, {
				headers: authHeader,
			})
			const result = await response.json()

			if (response.ok && result.success) {
				setCollections(result.collections)
				if (!allCollectionsScope && Array.isArray(result.selectedCollections)) {
					setSelectedCollections(result.selectedCollections)
				}
			} else {
				throw new Error(result.error || "Failed to load collections")
			}
		} catch (error) {
			setStatus({ type: "error", message: error instanceof Error ? error.message : "Failed to load collections" })
		} finally {
			setIsLoadingCollections(false)
		}
	}

	const loadSelectedCollections = async () => {
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(`${backendUrl}/api/v1/search/collections/selected?organizationId=${organizationId}`, {
				headers: authHeader,
			})
			const result = await response.json()

			if (response.ok && result.success) {
				// Extract collection names from the response
				// Backend returns array of objects: [{ name: "products", defaultSearchMethod: "keyword", ... }]
				const collectionNames = result.selectedCollections.map((item: any) =>
					typeof item === "string" ? item : item.name,
				)
				console.log("[SettingsPage] Loaded selected collections:", collectionNames)
				setSelectedCollections(collectionNames)
			}
		} catch (error) {
			console.error("Failed to load selected collections:", error)
		}
	}

	const saveSelectedCollections = async (selected: string[]) => {
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			await fetch(`${backendUrl}/api/v1/search/collections/select`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...authHeader,
				},
				body: JSON.stringify({
					organizationId,
					selectedCollections: selected,
				}),
			})
		} catch (error) {
			console.error("Failed to save selected collections:", error)
		}
	}

	const toggleCollectionSelection = async (collectionName: string) => {
		// Ensure we're working with string names only
		const currentNames = selectedCollections.map((c) => (typeof c === "string" ? c : (c as any).name))

		const newSelected = currentNames.includes(collectionName)
			? currentNames.filter((name) => name !== collectionName)
			: [...currentNames, collectionName]

		console.log("[SettingsPage] Toggling collection:", collectionName, "New selection:", newSelected)
		setSelectedCollections(newSelected)
		await saveSelectedCollections(newSelected)

		// Refresh the SearchProvider's collection list
		await refreshCollections()
	}

	const toggleCollectionExpanded = async (collectionName: string) => {
		const newExpanded = new Set(expandedCollections)

		if (newExpanded.has(collectionName)) {
			newExpanded.delete(collectionName)
		} else {
			newExpanded.add(collectionName)

			// Load health and samples if not already loaded
			if (!collectionHealth.has(collectionName)) {
				await loadCollectionHealth(collectionName)
			}
			if (!collectionSamples.has(collectionName)) {
				await loadCollectionSamples(collectionName)
			}
			// Load intent chips if not already loaded
			if (!collectionIntentChips.has(collectionName)) {
				await loadCollectionIntentChips(collectionName)
			}
		}

		setExpandedCollections(newExpanded)
	}

	const loadCollectionHealth = async (collectionName: string) => {
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(`${backendUrl}/api/v1/search/collections/${collectionName}/health`, {
				headers: authHeader,
			})
			const result = await response.json()

			if (response.ok && result.success) {
				setCollectionHealth((prev) => new Map(prev).set(collectionName, result.health))
			}
		} catch (error) {
			console.error(`Failed to load health for ${collectionName}:`, error)
		}
	}

	const loadCollectionSamples = async (collectionName: string) => {
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(
				`${backendUrl}/api/v1/search/collections/${collectionName}/samples?limit=3&organizationId=${organizationId}`,
				{ headers: authHeader },
			)
			const result = await response.json()

			if (response.ok && result.success) {
				setCollectionSamples((prev) => new Map(prev).set(collectionName, result.samples))
			}
		} catch (error) {
			console.error(`Failed to load samples for ${collectionName}:`, error)
		}
	}

	const loadCollectionSearchConfig = async (collectionName: string) => {
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(
				`${backendUrl}/api/v1/search/collections/${encodeURIComponent(collectionName)}/search-config?organizationId=${organizationId}`,
				{ headers: authHeader },
			)
			const result = await response.json()

			if (response.ok && result.success && result.config) {
				setCollectionSearchConfigs((prev) => new Map(prev).set(collectionName, result.config))
			}
		} catch (error) {
			console.error(`Failed to load search config for ${collectionName}:`, error)
		}
	}

	const updateCollectionDefaultCurrency = (collectionName: string, value: string) => {
		setCollectionSearchConfigs((prev) => {
			const current = prev.get(collectionName)
			if (!current) return prev

			const next = new Map(prev)
			next.set(collectionName, {
				...current,
				defaultCurrency: value.toUpperCase(),
			})
			return next
		})
	}

	const saveCollectionDefaultCurrency = async (collectionName: string) => {
		const config = collectionSearchConfigs.get(collectionName)
		if (!config) return

		setSavingCollectionCurrency((prev) => new Set(prev).add(collectionName))
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(
				`${backendUrl}/api/v1/search/collections/${encodeURIComponent(collectionName)}/search-config`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						...authHeader,
					},
					body: JSON.stringify({
						organizationId,
						searchCapabilities: config.searchCapabilities,
						defaultSearchMethod: config.defaultSearchMethod,
						defaultCurrency: config.defaultCurrency?.trim() ? config.defaultCurrency.trim().toUpperCase() : undefined,
						vectorQueryConfig: config.vectorQueryConfig,
						embeddingConfig: config.embeddingConfig,
						nlSearchConfig: config.nlSearchConfig,
					}),
				},
			)
			const result = await response.json()

			if (!response.ok || !result.success) {
				throw new Error(result.error || "Failed to update collection currency")
			}

			setCollectionSearchConfigs((prev) => new Map(prev).set(collectionName, result.config || config))
			setStatus({ type: "success", message: `Updated currency override for '${collectionName}'` })
		} catch (error) {
			setStatus({
				type: "error",
				message: error instanceof Error ? error.message : "Failed to update collection currency",
			})
		} finally {
			setSavingCollectionCurrency((prev) => {
				const next = new Set(prev)
				next.delete(collectionName)
				return next
			})
		}
	}

	const handleDeleteCollection = async (collectionName: string, belongsToCurrentOrg: boolean = true) => {
		if (!belongsToCurrentOrg) {
			setStatus({
				type: "error",
				message: `Cannot delete collection "${collectionName}" because it belongs to another organization.`,
			})
			return
		}

		if (!confirm(`Are you sure you want to delete the collection "${collectionName}"? This action cannot be undone.`)) {
			return
		}

		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(`${backendUrl}/api/v1/search/collections/${collectionName}`, {
				method: "DELETE",
				headers: authHeader,
			})
			const result = await response.json()

			if (response.ok && result.success) {
				setStatus({ type: "success", message: `Collection "${collectionName}" deleted successfully` })
				await loadCollections()

				// Remove from selected if it was selected
				if (selectedCollections.includes(collectionName)) {
					const newSelected = selectedCollections.filter((name) => name !== collectionName)
					setSelectedCollections(newSelected)
					saveSelectedCollections(newSelected)
				}
			} else {
				throw new Error(result.error || "Failed to delete collection")
			}
		} catch (error) {
			setStatus({ type: "error", message: error instanceof Error ? error.message : "Failed to delete collection" })
		}
	}

	const handleCreateCollection = async () => {
		if (!newCollectionName.trim()) {
			setStatus({ type: "error", message: "Collection name is required" })
			return
		}

		// Validate at least one search method is enabled
		if (!enableKeyword && !enableSemantic && !enableHybrid && !enableNaturalLanguage) {
			setStatus({ type: "error", message: "At least one search method must be enabled" })
			return
		}

		try {
			const toOptionalNumber = (raw: string): number | undefined => {
				const value = raw.trim()
				if (!value) return undefined
				const parsed = Number(value)
				return Number.isFinite(parsed) ? parsed : undefined
			}

			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()

			// Build search config
			const searchConfig: any = {
				enableKeyword,
				enableSemantic,
				enableHybrid,
				enableNaturalLanguage,
				defaultMethod,
				defaultCurrency: newCollectionDefaultCurrency.trim()
					? newCollectionDefaultCurrency.trim().toUpperCase()
					: undefined,
			}

			// Add embedding config if semantic or hybrid is enabled
			if (enableSemantic || enableHybrid) {
				searchConfig.embeddingConfig = {
					provider: embeddingProvider,
					modelName: embeddingModel,
				}

				// Only include API key if provided
				if (embeddingApiKey.trim()) {
					searchConfig.embeddingConfig.apiKey = embeddingApiKey.trim()
				}

				searchConfig.vectorQueryConfig = {
					...(enableSemantic
						? {
								semantic: {
									enabled: semanticVectorEnabled,
									k: toOptionalNumber(semanticVectorK),
									distanceThreshold: toOptionalNumber(semanticVectorDistanceThreshold),
								},
							}
						: {}),
					...(enableHybrid
						? {
								hybrid: {
									enabled: hybridVectorEnabled,
									k: toOptionalNumber(hybridVectorK),
									distanceThreshold: toOptionalNumber(hybridVectorDistanceThreshold),
									alpha: toOptionalNumber(hybridVectorAlpha),
									rerankHybridMatches: hybridRerankMatches,
								},
							}
						: {}),
				}
			}

			// Add NL config if natural language is enabled
			if (enableNaturalLanguage) {
				searchConfig.nlSearchConfig = {
					modelId: nlModelId,
					enableDebug: false,
					promptCacheTTL: 86400,
				}
			}

			const response = await fetch(`${backendUrl}/api/v1/search/collections`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...authHeader,
				},
				body: JSON.stringify({
					name: newCollectionName.trim(),
					template: newCollectionTemplate,
					organizationId,
					searchConfig,
				}),
			})
			const result = await response.json()

			if (response.ok && result.success) {
				setStatus({ type: "success", message: `Collection "${newCollectionName}" created successfully` })
				setShowCreateModal(false)
				setNewCollectionName("")
				setNewCollectionTemplate("products")
				// Reset search config
				setEnableKeyword(true)
				setEnableSemantic(false)
				setEnableHybrid(false)
				setEnableNaturalLanguage(false)
				setDefaultMethod("keyword")
				setEmbeddingProvider("openai")
				setEmbeddingModel("text-embedding-3-small")
				setEmbeddingApiKey("")
				setSemanticVectorEnabled(false)
				setSemanticVectorK("100")
				setSemanticVectorDistanceThreshold("")
				setHybridVectorEnabled(false)
				setHybridVectorK("100")
				setHybridVectorDistanceThreshold("")
				setHybridVectorAlpha("0.3")
				setHybridRerankMatches(false)
				setNewCollectionDefaultCurrency("")
				setNlModelId(availableNLModels.length > 0 ? availableNLModels[0].id : "openai-default")
				await loadCollections()
			} else {
				throw new Error(result.error || "Failed to create collection")
			}
		} catch (error) {
			setStatus({ type: "error", message: error instanceof Error ? error.message : "Failed to create collection" })
		}
	}

	const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		setIsLoading(true)
		setStatus(null)
		setUploadStats(null)

		try {
			const text = await file.text()
			const json = JSON.parse(text)

			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(`${backendUrl}/api/v1/search/admin/upload`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...authHeader,
				},
				body: JSON.stringify({
					products: json,
					organizationId: organizationId,
				}),
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error || "Upload failed")
			}

			setUploadStats({ count: result.count })
			setStatus({ type: "success", message: `Products uploaded successfully for organization: ${organizationId}` })
		} catch (error) {
			setStatus({ type: "error", message: error instanceof Error ? error.message : "Failed to upload file" })
		} finally {
			setIsLoading(false)
			// Reset file input
			event.target.value = ""
		}
	}

	const handleReindex = async () => {
		setIsLoading(true)
		setStatus(null)
		setReindexStats(null)

		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(`${backendUrl}/api/v1/search/admin/reindex`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...authHeader,
				},
				body: JSON.stringify({
					organizationId: organizationId,
				}),
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error || "Reindex failed")
			}

			setReindexStats({ products: result.products, suggestions: result.suggestions })
			setStatus({ type: "success", message: `Reindexing completed successfully for organization: ${organizationId}` })
		} catch (error) {
			setStatus({ type: "error", message: error instanceof Error ? error.message : "Failed to reindex" })
		} finally {
			setIsLoading(false)
		}
	}

	// === INTENT CHIPS FUNCTIONS ===

	const loadCollectionIntentChips = async (collectionName: string) => {
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			// GET is public — no auth required
			const response = await fetch(
				`${backendUrl}/api/v1/search/collections/${collectionName}/config/intent-chips?organizationId=${organizationId}`,
			)
			const result = await response.json()

			if (response.ok && result.success) {
				const chips: IntentChip[] = result.intentChips || []
				setCollectionIntentChips((prev) => new Map(prev).set(collectionName, chips))
				// Initialise the "new chip" form for this collection
				setNewChipInputs((prev) => {
					const next = new Map(prev)
					if (!next.has(collectionName)) {
						next.set(collectionName, { label: "", query: "", category: "" })
					}
					return next
				})
			}
		} catch (error) {
			console.error(`Failed to load intent chips for ${collectionName}:`, error)
		}
	}

	const saveCollectionIntentChips = async (collectionName: string) => {
		const chips = collectionIntentChips.get(collectionName)
		if (!chips) return

		setIntentChipsSaving((prev) => new Set(prev).add(collectionName))
		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(`${backendUrl}/api/v1/search/collections/${collectionName}/config/intent-chips`, {
				method: "PUT",
				headers: { "Content-Type": "application/json", ...authHeader },
				body: JSON.stringify({ intentChips: chips }),
			})
			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error || "Failed to save intent chips")
			}

			// Update local state with persisted result
			setCollectionIntentChips((prev) => new Map(prev).set(collectionName, result.intentChips || chips))
			setStatus({ type: "success", message: `Intent chips saved for "${collectionName}"` })
		} catch (error) {
			setStatus({
				type: "error",
				message: error instanceof Error ? error.message : "Failed to save intent chips",
			})
		} finally {
			setIntentChipsSaving((prev) => {
				const next = new Set(prev)
				next.delete(collectionName)
				return next
			})
		}
	}

	const addIntentChip = (collectionName: string) => {
		const input = newChipInputs.get(collectionName)
		if (!input || !input.label.trim() || !input.query.trim()) return

		const newChip: IntentChip = {
			label: input.label.trim(),
			query: input.query.trim(),
			...(input.category.trim() ? { category: input.category.trim() } : {}),
		}

		setCollectionIntentChips((prev) => {
			const existing = prev.get(collectionName) || []
			return new Map(prev).set(collectionName, [...existing, newChip])
		})

		// Reset the new chip input for this collection
		setNewChipInputs((prev) => new Map(prev).set(collectionName, { label: "", query: "", category: "" }))
	}

	const removeIntentChip = (collectionName: string, index: number) => {
		setCollectionIntentChips((prev) => {
			const existing = prev.get(collectionName) || []
			return new Map(prev).set(
				collectionName,
				existing.filter((_, i) => i !== index),
			)
		})
	}

	const updateNewChipInput = (collectionName: string, field: keyof NewChipInput, value: string) => {
		setNewChipInputs((prev) => {
			const current = prev.get(collectionName) || { label: "", query: "", category: "" }
			return new Map(prev).set(collectionName, { ...current, [field]: value })
		})
	}

	const handleCollectionUpload = async (event: React.ChangeEvent<HTMLInputElement>, collectionName: string) => {
		const file = event.target.files?.[0]
		if (!file) return

		setIsLoading(true)
		setStatus(null)

		try {
			const text = await file.text()
			const documents = JSON.parse(text)

			// Validate that it's an array
			if (!Array.isArray(documents)) {
				throw new Error("Invalid format: Expected an array of documents")
			}

			const backendUrl = import.meta.env.VITE_BACKEND_URL || ""
			const authHeader = await getAuthHeader()
			const response = await fetch(`${backendUrl}/api/v1/search/admin/upload`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...authHeader,
				},
				body: JSON.stringify({
					documents,
					organizationId,
					collectionName,
				}),
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error || "Upload failed")
			}

			setStatus({
				type: "success",
				message: `Successfully uploaded ${result.count} documents to ${collectionName}`,
			})

			// Reload collections to update document counts
			await loadCollections()

			// Reload samples for this collection
			await loadCollectionSamples(collectionName)
		} catch (error) {
			setStatus({
				type: "error",
				message: error instanceof Error ? error.message : "Failed to upload documents",
			})
		} finally {
			setIsLoading(false)
			// Reset file input
			event.target.value = ""
		}
	}

	return (
		<div className="container mx-auto px-4 py-6 max-w-4xl">
			{/* Header */}
			<div className="flex items-center gap-4 mb-8">
				<button
					aria-label="Back to search"
					className="p-2 rounded-full hover:bg-accent transition-colors"
					onClick={onBack}>
					<ArrowLeft className="w-5 h-5" />
				</button>
				<div className="flex-1">
					<h1 className="text-2xl font-bold">Search Settings</h1>
					<p className="text-muted-foreground">Manage your search configuration and data</p>
					<div className="mt-2 flex items-center gap-2">
						<span className="text-xs font-medium text-muted-foreground">Organization ID:</span>
						<code className="text-xs bg-muted px-2 py-1 rounded font-mono">{organizationId}</code>
					</div>
				</div>
				<button
					className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
					disabled={isPublishing}
					onClick={handlePublish}>
					{isPublishing ? "Publishing..." : "Publish"}
				</button>
			</div>

			<section className="bg-card border border-border rounded-lg p-4 mb-6">
				<div className="flex flex-col gap-2">
					<label className="text-sm font-medium" htmlFor="publish-url-input">
						Publish target URL
					</label>
					<input
						className="w-full border rounded px-3 py-2 bg-background"
						id="publish-url-input"
						onChange={(e) => setPublishUrlInput(e.target.value)}
						placeholder="https://examplestore.com"
						value={publishUrlInput}
					/>
					<p className="text-xs text-muted-foreground">
						Publish will create/update your org directory on the configured remote server, then open the store URL.
					</p>
				</div>
			</section>

			{/* A sign-in or the operator key also authorizes settings changes; otherwise ask for the store key. */}
			{(CLERK_ENABLED || !operatorKey) && <AdminKeysPanel kinds={["admin"]} />}
			<ShortlistSettings getAuthHeader={getAuthHeader} storeUrl={normalizePublishUrl(storeUrl || publishUrlInput)} />
			<ProductEnrichmentStudio key={storeUrl || publishUrlInput} storeUrl={normalizePublishUrl(storeUrl || publishUrlInput)} />
				<BrandPreviewSettings getAuthHeader={getAuthHeader} />
			<BrandThemeSettings getAuthHeader={getAuthHeader} storeUrl={normalizePublishUrl(storeUrl || publishUrlInput)} />
			<SmartSearchSettings
				activePlaceholders={sanitizeSearchPlaceholders(searchPlaceholders)}
				getAuthHeader={getAuthHeader}
				onPlaceholdersChanged={loadSearchPlaceholders}
				organizationId={organizationId}
			/>
			<section className="bg-card border border-border rounded-lg p-6 mb-6">
				<div className="flex items-center justify-between mb-3">
					<div>
						<h2 className="text-lg font-semibold">Search Placeholder Management</h2>
						<p className="text-sm text-muted-foreground">
							Configure floating search suggestions shown in the search widget.
						</p>
					</div>
					<button
						className="px-3 py-2 border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50"
						disabled={isLoadingSearchPlaceholders || isSavingSearchPlaceholders}
						onClick={resetSearchPlaceholders}>
						Reset Defaults
					</button>
				</div>

				{isLoadingSearchPlaceholders ? (
					<div className="text-sm text-muted-foreground">Loading search placeholders...</div>
				) : (
					<div className="space-y-2">
						{searchPlaceholders.map((placeholder, idx) => (
							<div className="flex items-center gap-2" key={`placeholder-${idx}`}>
								<input
									className="flex-1 border border-border rounded px-3 py-2 bg-background text-sm"
									maxLength={120}
									onChange={(e) => updateSearchPlaceholder(idx, e.target.value)}
									placeholder={`Placeholder ${idx + 1}`}
									value={placeholder}
								/>
								<button
									className="p-2 border border-border rounded hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
									disabled={isSavingSearchPlaceholders || searchPlaceholders.length <= 1}
									onClick={() => removeSearchPlaceholder(idx)}
									title="Remove placeholder">
									<Trash2 className="w-4 h-4" />
								</button>
							</div>
						))}
					</div>
				)}

				<div className="mt-3 text-xs text-muted-foreground">
					{sanitizeSearchPlaceholders(searchPlaceholders).length}/{MAX_SEARCH_PLACEHOLDERS} active placeholders
					(empty/duplicate values are auto-cleaned on save)
				</div>

				<div className="mt-4 flex items-center gap-2">
					<button
						className="px-3 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors disabled:opacity-50 flex items-center gap-2"
						disabled={isSavingSearchPlaceholders || searchPlaceholders.length >= MAX_SEARCH_PLACEHOLDERS}
						onClick={addSearchPlaceholder}>
						<Plus className="w-4 h-4" />
						Add Placeholder
					</button>
					<button
						className="px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
						disabled={isSavingSearchPlaceholders || isLoadingSearchPlaceholders}
						onClick={saveSearchPlaceholders}>
						{isSavingSearchPlaceholders ? (
							<RefreshCw className="w-4 h-4 animate-spin" />
						) : (
							<Save className="w-4 h-4" />
						)}
						{isSavingSearchPlaceholders ? "Saving..." : "Save Placeholders"}
					</button>
				</div>
			</section>

			<div className="space-y-6">
				{/* Data Management Section - REMOVED */}
				{/* Old upload flow removed - use per-collection upload instead */}

				{/* Status Message */}
				{status && (
					<div
						className={cn(
							"p-4 rounded-md flex items-center gap-3",
							status.type === "success" ? "bg-green-50 text-green-900" : "bg-red-50 text-red-900",
						)}>
						{status.type === "success" ? (
							<CheckCircle className="w-5 h-5 shrink-0" />
						) : (
							<AlertCircle className="w-5 h-5 shrink-0" />
						)}
						<p className="font-medium">{status.message}</p>
					</div>
				)}

				{/* Collection Management Section */}
				<section className="bg-card border border-border rounded-lg p-6">
					<div className="flex items-center justify-between mb-4">
						<div>
							<h2 className="text-lg font-semibold">Collection Management</h2>
							<p className="text-sm text-muted-foreground">
								Manage search collections and select which to search
								<span className="ml-2 text-xs">
									({showAllCollections ? "Showing all collections" : "Showing my store collections"})
								</span>
							</p>
						</div>
						<div className="flex gap-2">
							<button
								className="px-3 py-2 border border-border rounded-md hover:bg-accent transition-colors"
								onClick={() => setShowAllCollections((prev) => !prev)}>
								{showAllCollections ? "Show My Store" : "Show All"}
							</button>
							<button
								className="px-3 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors disabled:opacity-50 flex items-center gap-2"
								disabled={isLoadingCollections}
								onClick={() => loadCollections(showAllCollections)}>
								<RefreshCw className={cn("w-4 h-4", isLoadingCollections && "animate-spin")} />
								Refresh
							</button>
							<button
								className="px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors flex items-center gap-2"
								onClick={() => setShowCreateModal(true)}>
								<Plus className="w-4 h-4" />
								Create Collection
							</button>
						</div>
					</div>

					{isLoadingCollections ? (
						<div className="flex items-center justify-center py-12">
							<RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
						</div>
					) : collections.length === 0 ? (
						<div className="text-center py-12 text-muted-foreground">
							<Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
							<p>No collections found</p>
						</div>
					) : (
						<div className="space-y-3">
							{collections.map((collection) => {
								const physicalCollectionName = getPhysicalCollectionName(collection)
								const logicalCollectionName = getLogicalCollectionName(collection)
								const belongsToCurrentOrg = collectionBelongsToCurrentOrg(collection)
								const canSelect = !showAllCollections || belongsToCurrentOrg
								const isExpanded = expandedCollections.has(physicalCollectionName)
								const isSelected = selectedCollections.includes(logicalCollectionName)
								const health = collectionHealth.get(physicalCollectionName)
								const samples = collectionSamples.get(physicalCollectionName)

								return (
									<div className="border border-border rounded-lg overflow-hidden" key={physicalCollectionName}>
										{/* Collection Header */}
										<div className="p-4 bg-card hover:bg-accent/5 transition-colors">
											<div className="flex items-center gap-3">
												{/* Checkbox */}
												<input
													checked={isSelected}
													className="w-4 h-4 rounded border-border"
													disabled={!canSelect}
													onChange={() => toggleCollectionSelection(logicalCollectionName)}
													type="checkbox"
												/>

												{/* Expand/Collapse Button */}
												<button
													className="p-1 hover:bg-accent rounded transition-colors"
													onClick={async () => {
														if (
															!isExpanded &&
															belongsToCurrentOrg &&
															!collectionSearchConfigs.has(logicalCollectionName)
														) {
															await loadCollectionSearchConfig(logicalCollectionName)
														}
														await toggleCollectionExpanded(physicalCollectionName)
													}}>
													{isExpanded ? (
														<ChevronDown className="w-4 h-4" />
													) : (
														<ChevronRight className="w-4 h-4" />
													)}
												</button>

												{/* Collection Info */}
												<div className="flex-1">
													<div className="flex items-center gap-2">
														<h3 className="font-medium">{logicalCollectionName}</h3>
														{physicalCollectionName !== logicalCollectionName && (
															<span className="font-mono text-xs text-muted-foreground">
																{physicalCollectionName}
															</span>
														)}
														{showAllCollections && !belongsToCurrentOrg && (
															<span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
																other org
															</span>
														)}
														{health && (
															<div
																className={cn(
																	"w-2 h-2 rounded-full",
																	health.healthy ? "bg-green-500" : "bg-red-500",
																)}
																title={health.healthy ? "Healthy" : health.error}
															/>
														)}
													</div>
													<div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
														<span>{collection.numDocuments.toLocaleString()} documents</span>
														<span>•</span>
														<span>{new Date(collection.createdAt * 1000).toLocaleDateString()}</span>
														{collection.defaultSortingField && (
															<>
																<span>•</span>
																<span>Sort: {collection.defaultSortingField}</span>
															</>
														)}
													</div>
												</div>

												{/* Delete Button */}
												<button
													className="p-2 hover:bg-red-50 hover:text-red-600 rounded transition-colors"
													onClick={() =>
														handleDeleteCollection(logicalCollectionName, belongsToCurrentOrg)
													}
													title="Delete collection">
													<Trash2 className="w-4 h-4" />
												</button>
											</div>
										</div>

										{/* Expanded Content */}
										{isExpanded && (
											<div className="border-t border-border bg-muted/30 p-4 space-y-4">
												{/* Organization Stats */}
												{collection.organizationStats &&
													Object.keys(collection.organizationStats).length > 0 && (
														<div>
															<h4 className="text-sm font-medium mb-2">
																Organization Distribution
															</h4>
															<div className="grid grid-cols-2 gap-2">
																{Object.entries(collection.organizationStats).map(
																	([orgId, count]) => (
																		<div className="text-sm bg-card p-2 rounded" key={orgId}>
																			<span className="font-mono text-xs text-muted-foreground">
																				{orgId}
																			</span>
																			<span className="ml-2">{count} docs</span>
																		</div>
																	),
																)}
															</div>
														</div>
													)}

												{/* Schema Viewer */}
												<div>
													<h4 className="text-sm font-medium mb-2">
														Schema ({collection.fields.length} fields)
													</h4>
													<div className="bg-card rounded border border-border overflow-hidden">
														<div className="max-h-48 overflow-y-auto">
															<table className="w-full text-sm">
																<thead className="bg-muted sticky top-0">
																	<tr>
																		<th className="text-left p-2 font-medium">Field</th>
																		<th className="text-left p-2 font-medium">Type</th>
																		<th className="text-left p-2 font-medium">Properties</th>
																	</tr>
																</thead>
																<tbody>
																	{collection.fields.map((field) => (
																		<tr className="border-t border-border" key={field.name}>
																			<td className="p-2 font-mono text-xs">
																				{field.name}
																			</td>
																			<td className="p-2 text-xs">{field.type}</td>
																			<td className="p-2 text-xs">
																				<div className="flex gap-1">
																					{field.facet && (
																						<span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
																							facet
																						</span>
																					)}
																					{field.optional && (
																						<span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
																							optional
																						</span>
																					)}
																					{!field.index && (
																						<span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">
																							no-index
																						</span>
																					)}
																				</div>
																			</td>
																		</tr>
																	))}
																</tbody>
															</table>
														</div>
													</div>
												</div>

												{/* Sample Documents */}
												{samples && samples.length > 0 && (
													<div>
														<h4 className="text-sm font-medium mb-2">
															Sample Documents ({samples.length})
														</h4>
														<div className="space-y-2">
															{samples.map((doc, idx) => (
																<details
																	className="bg-card rounded border border-border"
																	key={idx}>
																	<summary className="p-2 cursor-pointer hover:bg-accent/5 text-sm font-medium">
																		Document {idx + 1}
																	</summary>
																	<div className="p-2 border-t border-border">
																		<pre className="text-xs overflow-x-auto bg-muted p-2 rounded">
																			{JSON.stringify(doc, null, 2)}
																		</pre>
																	</div>
																</details>
															))}
														</div>
													</div>
												)}

												{/* Upload Documents Button */}
												<div>
													<h4 className="text-sm font-medium mb-2">Upload Documents</h4>
													<div className="border border-dashed border-border rounded-lg p-4 hover:bg-accent/5 transition-colors relative">
														<input
															accept=".json"
															className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
															disabled={isLoading}
															onChange={(e) => handleCollectionUpload(e, collection.name)}
															type="file"
														/>
														<div className="flex items-center gap-3 pointer-events-none">
															<div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
																<Plus className="w-5 h-5 text-primary" />
															</div>
															<div>
																<p className="text-sm font-medium">Upload JSON documents</p>
																<p className="text-xs text-muted-foreground">
																	Click or drag to upload documents to this collection
																</p>
															</div>
														</div>
													</div>
												</div>

												{/* Currency Override */}
												{belongsToCurrentOrg && (
													<div>
														<h4 className="text-sm font-medium mb-2">Currency Override (fallback)</h4>
														<p className="text-xs text-muted-foreground mb-2">
															Used only when a product document has missing currency.
														</p>
														<div className="flex items-center gap-2">
															<input
																className="w-40 px-2 py-1.5 border border-border rounded text-xs uppercase"
																maxLength={3}
																onChange={(e) =>
																	updateCollectionDefaultCurrency(
																		logicalCollectionName,
																		e.target.value,
																	)
																}
																placeholder="e.g. INR"
																value={
																	collectionSearchConfigs.get(logicalCollectionName)
																		?.defaultCurrency || ""
																}
															/>
															<button
																className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded text-xs hover:bg-secondary/80 transition-colors disabled:opacity-50"
																disabled={savingCollectionCurrency.has(logicalCollectionName)}
																onClick={() =>
																	saveCollectionDefaultCurrency(logicalCollectionName)
																}>
																{savingCollectionCurrency.has(logicalCollectionName)
																	? "Saving..."
																	: "Save Currency"}
															</button>
														</div>
													</div>
												)}

												{/* Intent Chips Configuration */}
												<div>
													<div className="flex items-center justify-between mb-2">
														<h4 className="text-sm font-medium">Intent Chips</h4>
														<button
															className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-xs font-medium disabled:opacity-50"
															disabled={intentChipsSaving.has(collection.name)}
															onClick={() => saveCollectionIntentChips(collection.name)}
															title="Save intent chips to backend">
															{intentChipsSaving.has(collection.name) ? (
																<RefreshCw className="w-3.5 h-3.5 animate-spin" />
															) : (
																<Save className="w-3.5 h-3.5" />
															)}
															{intentChipsSaving.has(collection.name) ? "Saving…" : "Save"}
														</button>
													</div>
													<p className="text-xs text-muted-foreground mb-3">
														Configure the intent chips displayed in the search experience for this
														collection. Changes are saved locally until you click Save.
													</p>

													{/* Existing chips */}
													<div className="space-y-2 mb-3">
														{(collectionIntentChips.get(collection.name) || []).length === 0 ? (
															<p className="text-xs text-muted-foreground italic py-2 text-center">
																No intent chips configured. Add chips below.
															</p>
														) : (
															(collectionIntentChips.get(collection.name) || []).map(
																(chip, idx) => (
																	<div
																		className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-2"
																		key={idx}>
																		<div className="flex-1 min-w-0">
																			<div className="flex items-center gap-2 flex-wrap">
																				<span className="text-xs font-semibold px-2 py-0.5 bg-primary/10 text-primary rounded-full shrink-0">
																					{chip.label}
																				</span>
																				{chip.category && (
																					<span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground rounded shrink-0">
																						{chip.category}
																					</span>
																				)}
																			</div>
																			<p className="text-xs text-muted-foreground mt-1 truncate">
																				{chip.query}
																			</p>
																		</div>
																		<button
																			className="p-1 hover:bg-red-50 hover:text-red-600 rounded transition-colors shrink-0"
																			onClick={() => removeIntentChip(collection.name, idx)}
																			title="Remove chip">
																			<X className="w-3.5 h-3.5" />
																		</button>
																	</div>
																),
															)
														)}
													</div>

													{/* Add new chip form */}
													<div className="border border-dashed border-border rounded-lg p-3 bg-muted/20">
														<p className="text-xs font-medium mb-2 text-muted-foreground">
															Add new chip
														</p>
														<div className="grid grid-cols-3 gap-2 mb-2">
															<input
																className="col-span-1 px-2 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary"
																onChange={(e) =>
																	updateNewChipInput(collection.name, "label", e.target.value)
																}
																onKeyDown={(e) => {
																	if (e.key === "Enter") addIntentChip(collection.name)
																}}
																placeholder="Label *"
																type="text"
																value={newChipInputs.get(collection.name)?.label || ""}
															/>
															<input
																className="col-span-1 px-2 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary"
																onChange={(e) =>
																	updateNewChipInput(collection.name, "query", e.target.value)
																}
																onKeyDown={(e) => {
																	if (e.key === "Enter") addIntentChip(collection.name)
																}}
																placeholder="Query *"
																type="text"
																value={newChipInputs.get(collection.name)?.query || ""}
															/>
															<input
																className="col-span-1 px-2 py-1.5 border border-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-primary"
																onChange={(e) =>
																	updateNewChipInput(
																		collection.name,
																		"category",
																		e.target.value,
																	)
																}
																onKeyDown={(e) => {
																	if (e.key === "Enter") addIntentChip(collection.name)
																}}
																placeholder="Category (opt)"
																type="text"
																value={newChipInputs.get(collection.name)?.category || ""}
															/>
														</div>
														<button
															className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary text-secondary-foreground rounded text-xs hover:bg-secondary/80 transition-colors disabled:opacity-50"
															disabled={
																!newChipInputs.get(collection.name)?.label?.trim() ||
																!newChipInputs.get(collection.name)?.query?.trim()
															}
															onClick={() => addIntentChip(collection.name)}>
															<Plus className="w-3.5 h-3.5" />
															Add Chip
														</button>
													</div>
												</div>
											</div>
										)}
									</div>
								)
							})}
						</div>
					)}
				</section>
			</div>

			{/* Create Collection Modal */}
			{showCreateModal && (
				<div
					className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
					onClick={() => setShowCreateModal(false)}>
					<div
						className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg p-6 max-w-md w-full shadow-2xl"
						onClick={(e) => e.stopPropagation()}>
						<h3 className="text-lg font-semibold mb-4">Create New Collection</h3>

						<div className="space-y-4 max-h-[70vh] overflow-y-auto">
							<div>
								<label className="block text-sm font-medium mb-2">Collection Name</label>
								<input
									className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
									onChange={(e) => setNewCollectionName(e.target.value)}
									placeholder="e.g., products_v2"
									type="text"
									value={newCollectionName}
								/>
							</div>

							<div>
								<label className="block text-sm font-medium mb-2">Template</label>
								{isLoadingTemplates ? (
									<div className="w-full px-3 py-2 border border-border rounded-md bg-muted flex items-center gap-2">
										<RefreshCw className="w-4 h-4 animate-spin" />
										<span className="text-muted-foreground">Loading templates...</span>
									</div>
								) : (
									<select
										className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
										onChange={(e) => setNewCollectionTemplate(e.target.value)}
										value={newCollectionTemplate}>
										{availableTemplates.map((template) => (
											<option key={template.name} value={template.name}>
												{template.name.charAt(0).toUpperCase() +
													template.name.slice(1).replace(/_/g, " ")}
											</option>
										))}
									</select>
								)}
								<p className="text-xs text-muted-foreground mt-1">
									{availableTemplates.find((t) => t.name === newCollectionTemplate)?.description ||
										"Select a template to define the collection schema"}
								</p>
							</div>

							{/* Search Capabilities Section */}
							<div className="border-t border-border pt-4">
								<h4 className="text-sm font-semibold mb-3">Search Capabilities</h4>
								<p className="text-xs text-muted-foreground mb-3">
									Select which search methods to enable for this collection
								</p>

								<div className="space-y-3">
									{/* Keyword Search */}
									<label className="flex items-start gap-3 cursor-pointer">
										<input
											checked={enableKeyword}
											className="mt-0.5 w-4 h-4 rounded border-border disabled:opacity-50"
											disabled={enableNaturalLanguage}
											onChange={(e) => {
												setEnableKeyword(e.target.checked)
												// If enabling keyword and semantic is disabled, set keyword as default
												if (e.target.checked && !enableSemantic) {
													setDefaultMethod("keyword")
												}
												// If disabling keyword and it's the default, switch default
												if (!e.target.checked && defaultMethod === "keyword") {
													if (enableSemantic) setDefaultMethod("semantic")
													else if (enableHybrid) setDefaultMethod("hybrid")
												}
												// Disable hybrid if keyword is disabled
												if (!e.target.checked && enableHybrid) {
													setEnableHybrid(false)
												}
											}}
											type="checkbox"
										/>
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium">Keyword Search</span>
												<span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
													Recommended
												</span>
											</div>
											<p className="text-xs text-muted-foreground mt-0.5">
												Traditional full-text search with BM25 ranking
											</p>
										</div>
									</label>

									{/* Semantic Search */}
									<label className="flex items-start gap-3 cursor-pointer">
										<input
											checked={enableSemantic}
											className="mt-0.5 w-4 h-4 rounded border-border disabled:opacity-50"
											disabled={enableNaturalLanguage}
											onChange={(e) => {
												setEnableSemantic(e.target.checked)
												// If enabling semantic and keyword is disabled, set semantic as default
												if (e.target.checked && !enableKeyword) {
													setDefaultMethod("semantic")
												}
												// If disabling semantic and it's the default, switch default
												if (!e.target.checked && defaultMethod === "semantic") {
													if (enableKeyword) setDefaultMethod("keyword")
													else if (enableHybrid) setDefaultMethod("hybrid")
												}
												// Disable hybrid if semantic is disabled
												if (!e.target.checked && enableHybrid) {
													setEnableHybrid(false)
												}
											}}
											type="checkbox"
										/>
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium">AI Search (Semantic)</span>
												<span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
													Requires API Key
												</span>
											</div>
											<p className="text-xs text-muted-foreground mt-0.5">
												Vector-based search using embeddings for semantic understanding
											</p>
										</div>
									</label>

									{/* Hybrid Search */}
									<label className="flex items-start gap-3 cursor-pointer">
										<input
											checked={enableHybrid}
											className="mt-0.5 w-4 h-4 rounded border-border disabled:opacity-50"
											disabled={!enableKeyword || !enableSemantic || enableNaturalLanguage}
											onChange={(e) => {
												setEnableHybrid(e.target.checked)
												// If disabling hybrid and it's the default, switch default
												if (!e.target.checked && defaultMethod === "hybrid") {
													if (enableKeyword) setDefaultMethod("keyword")
													else if (enableSemantic) setDefaultMethod("semantic")
												}
												// Auto-enable keyword and semantic if enabling hybrid
												if (e.target.checked) {
													setEnableKeyword(true)
													setEnableSemantic(true)
												}
											}}
											type="checkbox"
										/>
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium">Hybrid Search</span>
												<span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
													Best Results
												</span>
											</div>
											<p className="text-xs text-muted-foreground mt-0.5">
												Combines keyword and semantic search for optimal relevance
											</p>
											{(!enableKeyword || !enableSemantic) && (
												<p className="text-xs text-amber-600 mt-1">
													⚠️ Requires both Keyword and AI Search to be enabled
												</p>
											)}
										</div>
									</label>

									{/* Natural Language Search */}
									<label className="flex items-start gap-3 cursor-pointer">
										<input
											checked={enableNaturalLanguage}
											className="mt-0.5 w-4 h-4 rounded border-border disabled:opacity-50"
											disabled={enableKeyword || enableSemantic || enableHybrid}
											onChange={(e) => {
												setEnableNaturalLanguage(e.target.checked)
												// If enabling NL, disable all other methods and set as default
												if (e.target.checked) {
													setEnableKeyword(false)
													setEnableSemantic(false)
													setEnableHybrid(false)
													setDefaultMethod("natural_language")
												}
												// If disabling NL, enable keyword as fallback
												if (!e.target.checked) {
													setEnableKeyword(true)
													setDefaultMethod("keyword")
												}
											}}
											type="checkbox"
										/>
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium">Natural Language Search</span>
												<span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">
													LLM-Powered
												</span>
											</div>
											<p className="text-xs text-muted-foreground mt-0.5">
												Uses LLM to parse natural language queries into structured filters
											</p>
											{(enableKeyword || enableSemantic || enableHybrid) && (
												<p className="text-xs text-amber-600 mt-1">
													⚠️ Cannot be combined with other search methods
												</p>
											)}
										</div>
									</label>
								</div>
							</div>

							{/* Default Search Method */}
							<div className="border-t border-border pt-4">
								<label className="block text-sm font-medium mb-2">Default Search Method</label>
								<select
									className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
									onChange={(e) =>
										setDefaultMethod(e.target.value as "keyword" | "semantic" | "hybrid" | "natural_language")
									}
									value={defaultMethod}>
									{enableKeyword && <option value="keyword">Keyword Search</option>}
									{enableSemantic && <option value="semantic">AI Search (Semantic)</option>}
									{enableHybrid && <option value="hybrid">Hybrid Search</option>}
									{enableNaturalLanguage && <option value="natural_language">Natural Language Search</option>}
								</select>
								<p className="text-xs text-muted-foreground mt-1">
									The default method used when searching this collection
								</p>
							</div>

							{/* Natural Language Configuration (only show if NL is enabled) */}
							{enableNaturalLanguage && (
								<div className="border-t border-border pt-4">
									<h4 className="text-sm font-semibold mb-3">Natural Language Configuration</h4>
									<p className="text-xs text-muted-foreground mb-3">
										Select the LLM model for parsing natural language queries
									</p>

									<div>
										<label className="block text-sm font-medium mb-2">LLM Model</label>
										<select
											className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
											onChange={(e) => setNlModelId(e.target.value)}
											value={nlModelId}>
											{availableNLModels.map((model) => (
												<option key={model.id} value={model.id}>
													{model.provider} - {model.modelName}
												</option>
											))}
										</select>
										<p className="text-xs text-muted-foreground mt-1">
											{availableNLModels.find((m) => m.id === nlModelId)?.description ||
												"LLM model for natural language query parsing"}
										</p>
									</div>
								</div>
							)}

							{/* Embedding Configuration (only show if semantic or hybrid is enabled) */}
							{(enableSemantic || enableHybrid) && (
								<div className="border-t border-border pt-4">
									<h4 className="text-sm font-semibold mb-3">Embedding Configuration</h4>
									<p className="text-xs text-muted-foreground mb-3">
										Configure the AI model for generating embeddings
									</p>

									<div className="space-y-3">
										{/* Provider Selection */}
										<div>
											<label className="block text-sm font-medium mb-2">Provider</label>
											<select
												className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
												onChange={(e) => {
													setEmbeddingProvider(
														e.target.value as "openai" | "google" | "azure" | "custom",
													)
													// Set default model for provider
													if (e.target.value === "openai") setEmbeddingModel("text-embedding-3-small")
													else if (e.target.value === "google") setEmbeddingModel("text-embedding-004")
													else if (e.target.value === "azure")
														setEmbeddingModel("text-embedding-ada-002")
													else setEmbeddingModel("")
												}}
												value={embeddingProvider}>
												<option value="openai">OpenAI</option>
												<option value="google">Google (Vertex AI)</option>
												<option value="azure">Azure OpenAI</option>
												<option value="custom">Custom Provider</option>
											</select>
										</div>

										{/* Model Selection */}
										<div>
											<label className="block text-sm font-medium mb-2">Model</label>
											{embeddingProvider === "openai" ? (
												<select
													className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
													onChange={(e) => setEmbeddingModel(e.target.value)}
													value={embeddingModel}>
													<option value="text-embedding-3-small">
														text-embedding-3-small (Recommended)
													</option>
													<option value="text-embedding-3-large">
														text-embedding-3-large (Higher Quality)
													</option>
													<option value="text-embedding-ada-002">
														text-embedding-ada-002 (Legacy)
													</option>
												</select>
											) : embeddingProvider === "google" ? (
												<select
													className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
													onChange={(e) => setEmbeddingModel(e.target.value)}
													value={embeddingModel}>
													<option value="text-embedding-004">text-embedding-004 (Latest)</option>
													<option value="textembedding-gecko@003">textembedding-gecko@003</option>
												</select>
											) : (
												<input
													className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
													onChange={(e) => setEmbeddingModel(e.target.value)}
													placeholder="Enter model name"
													type="text"
													value={embeddingModel}
												/>
											)}
											<p className="text-xs text-muted-foreground mt-1">
												{embeddingProvider === "openai" && "OpenAI embedding models for semantic search"}
												{embeddingProvider === "google" && "Google Vertex AI embedding models"}
												{embeddingProvider === "azure" && "Azure OpenAI embedding deployment name"}
												{embeddingProvider === "custom" && "Custom embedding model identifier"}
											</p>
										</div>

										{/* API Key */}
										<div>
											<label className="block text-sm font-medium mb-2">API Key (Optional)</label>
											<input
												className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
												onChange={(e) => setEmbeddingApiKey(e.target.value)}
												placeholder="sk-..."
												type="password"
												value={embeddingApiKey}
											/>
											<p className="text-xs text-muted-foreground mt-1">
												Leave empty to use server-side API key. Provide a key for collection-specific
												configuration.
											</p>
										</div>
									</div>

									<div className="border-t border-border pt-4 mt-4 space-y-3">
										<h4 className="text-sm font-semibold">Vector Query Tuning</h4>
										<p className="text-xs text-muted-foreground">
											Optional tuning parameters for semantic/hybrid vector ranking.
										</p>

										{enableSemantic && (
											<div className="rounded border border-border p-3 space-y-2">
												<label className="flex items-center gap-2 text-sm">
													<input
														checked={semanticVectorEnabled}
														onChange={(e) => setSemanticVectorEnabled(e.target.checked)}
														type="checkbox"
													/>
													Enable semantic vector_query
												</label>
												<div className="grid grid-cols-2 gap-2">
													<input
														className="px-2 py-1.5 border border-border rounded text-xs"
														min="1"
														onChange={(e) => setSemanticVectorK(e.target.value)}
														placeholder="k (e.g. 100)"
														type="number"
														value={semanticVectorK}
													/>
													<input
														className="px-2 py-1.5 border border-border rounded text-xs"
														min="0"
														onChange={(e) => setSemanticVectorDistanceThreshold(e.target.value)}
														placeholder="distance_threshold"
														step="0.01"
														type="number"
														value={semanticVectorDistanceThreshold}
													/>
												</div>
											</div>
										)}

										{enableHybrid && (
											<div className="rounded border border-border p-3 space-y-2">
												<label className="flex items-center gap-2 text-sm">
													<input
														checked={hybridVectorEnabled}
														onChange={(e) => setHybridVectorEnabled(e.target.checked)}
														type="checkbox"
													/>
													Enable hybrid vector_query
												</label>
												<div className="grid grid-cols-2 gap-2">
													<input
														className="px-2 py-1.5 border border-border rounded text-xs"
														min="1"
														onChange={(e) => setHybridVectorK(e.target.value)}
														placeholder="k (e.g. 100)"
														type="number"
														value={hybridVectorK}
													/>
													<input
														className="px-2 py-1.5 border border-border rounded text-xs"
														min="0"
														onChange={(e) => setHybridVectorDistanceThreshold(e.target.value)}
														placeholder="distance_threshold"
														step="0.01"
														type="number"
														value={hybridVectorDistanceThreshold}
													/>
													<input
														className="px-2 py-1.5 border border-border rounded text-xs"
														max="1"
														min="0"
														onChange={(e) => setHybridVectorAlpha(e.target.value)}
														placeholder="alpha (0..1)"
														step="0.01"
														type="number"
														value={hybridVectorAlpha}
													/>
													<label className="flex items-center gap-2 text-xs border border-border rounded px-2 py-1.5">
														<input
															checked={hybridRerankMatches}
															onChange={(e) => setHybridRerankMatches(e.target.checked)}
															type="checkbox"
														/>
														rerank_hybrid_matches
													</label>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>

						<div className="flex gap-3 mt-6">
							<button
								className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-accent transition-colors"
								onClick={() => setShowCreateModal(false)}>
								Cancel
							</button>
							<button
								className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
								onClick={handleCreateCollection}>
								Create
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
